import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import AppTable from "../../components/app-table/AppTable";
import Iconify from "../../components/iconify/Iconify";
import { getCategorySalesSummary } from "../../reduxStore/slices/reportSlice";
import { useNotify } from "../../utils/snackbarUtils";
import { formatCurrency } from "../../utils/currency";

const CategorySalesReport = ({ startDate, endDate }) => {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { categorySalesSummary, loading: isReportLoading } = useSelector(
    (state) => state.report,
  );
  const storeId = user?.storeId;

  const fetchCategorySalesSummary = async () => {
    try {
      await dispatch(
        getCategorySalesSummary({
          storeId,
          startDate: startDate,
          endDate: endDate,
        }),
      ).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Fetch stats on mount and when storeId changes
  useEffect(() => {
    if (storeId) {
      fetchCategorySalesSummary();
    }
  }, [storeId, dispatch, startDate, endDate]);

  // Calculate total for percentage
  const totalSales = useMemo(
    () =>
      categorySalesSummary?.reduce(
        (sum, item) => sum + (item.gross_sales || 0),
        0,
      ) || 0,
    [categorySalesSummary],
  );

  // Transform data for table
  const tableRows =
    categorySalesSummary?.map((item) => ({
      categoryId: item.category_id,
      categoryName: item.category_name,
      quantity: item.total_items_sold,
      sales: item.gross_sales,
      share:
        totalSales > 0 ? ((item.gross_sales / totalSales) * 100).toFixed(2) : 0,
    })) || [];

  const tableHead = [
    { id: "categoryName", label: "Category Name", align: "left" },
    { id: "quantity", label: "Quantity", align: "left" },
    { id: "sales", label: "Sales (₹)", align: "left" },
    { id: "share", label: "% Share", align: "left" },
  ];

  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    xaxis: {
      categories: categorySalesSummary?.map((d) => d.category_name) || [],
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        horizontal: false,
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: {
        formatter: (val) => formatCurrency(val),
      },
    },
    title: {
      text: "Category wise Trend",
      align: "left",
      style: { fontSize: "14px", fontWeight: "bold" },
    },
    colors: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"],
    legend: { show: false },
  };

  const chartSeries = [
    {
      name: "Sales",
      data: categorySalesSummary?.map((d) => d.gross_sales) || [],
    },
  ];

  const handleCategoryExport = () => {
    const csvContent = [
      ["Category Name", "Quantity", "Sales (₹)", "% Share"],
      ...tableRows.map((row) => [
        row.categoryName,
        row.quantity,
        row.sales,
        row.share,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `category_sales_report_${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      showSuccess("Category sales exported successfully.");
    }, 1000);
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="bar"
          height={320}
        />
      </Paper>

      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            Category wise Sales Details
          </Typography>
          <AppButton
            variant="contained"
            startIcon={<Iconify icon="ic:round-download" />}
            disabled={tableRows.length === 0}
            onClick={handleCategoryExport}
          >
            Export
          </AppButton>
        </Box>
        <AppTable
          tableHead={tableHead}
          tableRows={tableRows}
          listType="categorySalesTables"
          searchPlaceholder="Search By Category Name"
          searchField={"categoryName"}
        />
      </Box>
    </>
  );
};

export default CategorySalesReport;
